import {
  type AdminGetExamCenterProctorRequestByIdQuery,
  ExamCenterProctorRequestStep,
  useAdminGetExamCenterProctorRequestByIdQuery,
} from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { type PropsWithOptionalChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';

export type ExamCenterProctorRequest =
  AdminGetExamCenterProctorRequestByIdQuery['ecAdminGetExamCenterProctorRequestById']['examCenterProctorRequest'];

interface AdminExamCenterProctorRequestDetailsContextValue {
  examCenterProctorRequest: ExamCenterProctorRequest;
}

interface AdminExamCenterProctorRequestDetailsProviderProps extends PropsWithOptionalChildren {
  examCenterProctorRequestId: string;
}

const AdminExamCenterProctorRequestDetailsContext = createContext<
  AdminExamCenterProctorRequestDetailsContextValue | undefined
>(undefined);

export default function AdminExamCenterProctorRequestDetailsProvider(
  props: Readonly<AdminExamCenterProctorRequestDetailsProviderProps>,
) {
  const { examCenterProctorRequestId, children } = props;

  const {
    data: getExamCenterProctorRequest,
    loading: isUseGetExamCenterProctorRequestByIdLoading,
  } = useAdminGetExamCenterProctorRequestByIdQuery({
    variables: {
      adminGetExamCenterProctorRequestByIdInput: {
        id: examCenterProctorRequestId,
      },
    },
  });

  const examCenterProctorRequest =
    getExamCenterProctorRequest?.ecAdminGetExamCenterProctorRequestById?.examCenterProctorRequest;

  const contextValue: AdminExamCenterProctorRequestDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      examCenterProctorRequest: examCenterProctorRequest!,
    } as AdminExamCenterProctorRequestDetailsContextValue; // TODO: add requestedBy
  }, [examCenterProctorRequest]);

  if (isUseGetExamCenterProctorRequestByIdLoading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (!examCenterProctorRequest) {
    return <Navigate to='/admin/exam-proctors/requests' />;
  }

  if (examCenterProctorRequest.step === ExamCenterProctorRequestStep.Approved) {
    return <Navigate to='/admin/exam-proctors/proctors' />;
  }

  return (
    <AdminExamCenterProctorRequestDetailsContext.Provider value={contextValue}>
      {children}
    </AdminExamCenterProctorRequestDetailsContext.Provider>
  );
}
export { AdminExamCenterProctorRequestDetailsContext };
